// ============================================================================
// ASNE C2 — UNIT CONVERSION UTILITIES
// ============================================================================
//
// All sensor data arrives and is stored in SI units (meters, m/s). These
// helpers convert to US-customary / imperial for display, and format values
// with the appropriate unit label. The conversion direction is always
// SI-in → display-out; we never store converted values.
//
// Supported unit systems:
//   'imperial' — feet for altitude, mph for speed, miles for distance
//   'metric'   — meters for altitude, m/s for speed, km for distance
//
// The active system is read from the `useUnitsStore` Zustand store, which
// persists per-user to localStorage. Components call the formatting helpers
// (fmtAltitude, fmtSpeed, etc.) and the hook returns locale-appropriate
// strings without the component caring which system is active.
// ============================================================================

export type UnitSystem = 'imperial' | 'metric';

// ---------------------------------------------------------------------------
// RAW CONVERSIONS — pure math, no formatting
// ---------------------------------------------------------------------------

/** Meters → Feet (1 m = 3.28084 ft) */
export const metersToFeet = (m: number): number => m * 3.28084;

/** Feet → Meters */
export const feetToMeters = (ft: number): number => ft / 3.28084;

/** m/s → mph (1 m/s = 2.23694 mph) */
export const msToMph = (ms: number): number => ms * 2.23694;

/** m/s → knots (1 m/s = 1.94384 kn) — used in aviation contexts */
export const msToKnots = (ms: number): number => ms * 1.94384;

/** Meters → Miles (1 m = 0.000621371 mi) */
export const metersToMiles = (m: number): number => m * 0.000621371;

/** Meters → Kilometers */
export const metersToKm = (m: number): number => m / 1000;

// ---------------------------------------------------------------------------
// FORMATTED DISPLAY — returns "123 ft" / "37.5 m" style strings
// ---------------------------------------------------------------------------

/**
 * Format an altitude value with unit label.
 * @param meters  Raw altitude in meters (from DB / sensor)
 * @param system  'imperial' | 'metric'
 * @param suffix  Optional qualifier appended after the unit, e.g. "MSL" or "AGL"
 * @param decimals Number of decimal places (default 0 for altitude)
 */
export function fmtAltitude(
  meters: number | null | undefined,
  system: UnitSystem,
  suffix?: string,
  decimals = 0
): string {
  if (meters == null) return 'N/A';
  const val = system === 'imperial' ? metersToFeet(meters) : meters;
  const unit = system === 'imperial' ? 'ft' : 'm';
  const label = suffix ? `${unit} ${suffix}` : unit;
  return `${val.toFixed(decimals)} ${label}`;
}

/**
 * Convert an altitude value without formatting (for chart data points).
 */
export function convertAltitude(meters: number, system: UnitSystem): number {
  return system === 'imperial' ? Math.round(metersToFeet(meters)) : Math.round(meters);
}

/**
 * The unit label for altitude axes / headers.
 */
export function altitudeUnit(system: UnitSystem, suffix?: string): string {
  const unit = system === 'imperial' ? 'ft' : 'm';
  return suffix ? `${unit} ${suffix}` : unit;
}

/**
 * Format a speed value with unit label.
 * Imperial uses mph (more intuitive for US law enforcement than knots).
 */
export function fmtSpeed(
  ms: number | null | undefined,
  system: UnitSystem,
  decimals = 1
): string {
  if (ms == null) return 'N/A';
  const val = system === 'imperial' ? msToMph(ms) : ms;
  const unit = system === 'imperial' ? 'mph' : 'm/s';
  return `${val.toFixed(decimals)} ${unit}`;
}

/**
 * Convert a speed value without formatting (for chart data points).
 */
export function convertSpeed(ms: number, system: UnitSystem): number {
  const val = system === 'imperial' ? msToMph(ms) : ms;
  return Math.round(val * 10) / 10;
}

/**
 * The unit label for speed axes / headers.
 */
export function speedUnit(system: UnitSystem): string {
  return system === 'imperial' ? 'mph' : 'm/s';
}

/**
 * Format a distance/radius value with unit label.
 * Small distances (<1 mi / <1 km) stay in feet/meters for readability.
 */
export function fmtDistance(
  meters: number | null | undefined,
  system: UnitSystem,
  decimals = 1
): string {
  if (meters == null) return 'N/A';
  if (system === 'imperial') {
    const miles = metersToMiles(meters);
    if (miles < 0.1) return `${Math.round(metersToFeet(meters))} ft`;
    return `${miles.toFixed(decimals)} mi`;
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${metersToKm(meters).toFixed(decimals)} km`;
}

// ---------------------------------------------------------------------------
// REFERENCE VALUES — well-known limits in both systems
// ---------------------------------------------------------------------------

/**
 * FAA 400 ft AGL drone altitude limit, expressed in the active unit system.
 * In metric this is ~122 m. The raw SI value stored is 121.92 m.
 */
export const DRONE_ALT_LIMIT_METERS = 121.92; // 400 ft exactly

export function droneAltLimit(system: UnitSystem): { value: number; label: string } {
  if (system === 'imperial') {
    return { value: 400, label: '400 ft AGL limit' };
  }
  return { value: 122, label: '122 m AGL limit (~400 ft)' };
}
